import { useState, useEffect, useRef } from "react";
import toast from "../../../utils/toast";
import {
  getSupportTicketList,
  getSupportTicketById,
  getSupportTicketMessageById,
  replySupportTicketMessage,
  startSupportTicketMessage,
  resolveSupportTicketMessage,
  closeSupportTicketMessage,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function SupportTicketReq() {
  const chatContainerRef = useRef(null);

  const [ticketData, setTicketData] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const searchTimer = useRef(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const FILTER_TABS = [
    { key: "all", label: "All", count: ticketData?.totalTickets },
    { key: "Open", label: "Open", count: ticketData?.open },
    { key: "InProgress", label: "In Progress", count: ticketData?.inProgress },
    { key: "Resolved", label: "Resolved", count: ticketData?.resolved },
    { key: "Closed", label: "Closed", count: ticketData?.closed },
  ];

  useEffect(() => {
    fetchTicketList();
  }, [activeFilter]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchTicketList = async (search = searchInput) => {
    setListLoading(true);
    try {
      const params = {};
      if (activeFilter !== "all") params.status = activeFilter;
      if (search) params.search = search;
      const res = await getSupportTicketList(params);
      if (res.statusCode === 1) setTicketData(res.data);
    } catch (err) {
      console.log("Failed to fetch tickets");
    } finally {
      setListLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchTicketList(value);
    }, 3000);
  };

  const handleReset = () => {
    setSearchInput("");
    if (searchTimer.current) clearTimeout(searchTimer.current);
    fetchTicketList("");
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith("0001")) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr || dateStr.startsWith("0001")) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    if (status === "Open") return { label: "🔵 Open", cls: "badge-info" };
    if (status === "InProgress")
      return { label: "🟡 In Progress", cls: "badge-pending" };
    if (status === "Resolved")
      return { label: "✅ Resolved", cls: "badge-active" };
    if (status === "Closed")
      return { label: "🔒  Closed", cls: "badge-closed" };
    return { label: status || "—", cls: "badge-gray" };
  };

  const getPriorityBadge = (priority) => {
    if (priority === "High") return { label: "High", cls: "badge-late" };
    if (priority === "Medium") return { label: "Medium", cls: "badge-pending" };
    if (priority === "Low") return { label: "Low", cls: "badge-teal" };
    return { label: priority || "—", cls: "badge-gray" };
  };

  const getPriorityBorder = (priority) => {
    if (priority === "High") return "#dc2626";
    if (priority === "Medium") return "#f59e0b";
    if (priority === "Low") return "#14b8a6";
    return "var(--border-light)";
  };

  // ── Detail Modal ──
  const openTicketDetail = async (ticketId) => {
    try {
      const res = await getSupportTicketById(ticketId);
      if (res.statusCode === 1) {
        setSelectedTicket(res.data);
        setShowDetailModal(true);
        fetchMessages(ticketId, false);
      }
    } catch (err) {
      toast.error("Failed to load ticket details.");
    }
  };

  const fetchMessages = async (ticketId, silent = false) => {
    if (!silent) setMessagesLoading(true);
    try {
      const res = await getSupportTicketMessageById(ticketId);
      if (res.statusCode === 1) setMessages(res.data);
    } catch (err) {
      console.log("Failed to fetch messages");
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTicket(null);
    setMessages([]);
    setReplyText("");
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error("Please type a message.");
      return;
    }

    const tempMessage = {
      messageId: Date.now(),
      ticketId: selectedTicket.ticketId,
      senderType: "Owner",
      senderId: selectedTicket.ownerId,
      message: replyText,
      isVoice: false,
      audioUrl: "",
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMessage]);
    const sentText = replyText;
    setReplyText("");

    setReplyLoading(true);
    try {
      const payload = {
        ticketId: selectedTicket.ticketId,
        message: sentText,
        isVoice: false,
        audioUrl: "",
      };
      const res = await replySupportTicketMessage(payload);
      if (res.statusCode === 1) {
        fetchMessages(selectedTicket.ticketId, true);
      } else {
        toast.error(res.message || "Failed to send message.");
        setMessages((prev) =>
          prev.filter((m) => m.messageId !== tempMessage.messageId),
        );
        setReplyText(sentText);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setMessages((prev) =>
        prev.filter((m) => m.messageId !== tempMessage.messageId),
      );
      setReplyText(sentText);
    } finally {
      setReplyLoading(false);
    }
  };

  const handleStatusAction = async (action) => {
    setActionLoading(true);
    try {
      let res;
      if (action === "start")
        res = await startSupportTicketMessage(selectedTicket.ticketId);
      if (action === "resolve")
        res = await resolveSupportTicketMessage(selectedTicket.ticketId);
      if (action === "close")
        res = await closeSupportTicketMessage(selectedTicket.ticketId);

      if (res.statusCode === 1) {
        toast.success(res.message);
        // Update selected ticket status locally
        const newStatus =
          action === "start"
            ? "InProgress"
            : action === "resolve"
              ? "Resolved"
              : "Closed";
        setSelectedTicket((prev) => ({ ...prev, status: newStatus }));
        fetchTicketList();
      } else {
        toast.error(res.message || "Action failed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Support Tickets</div>
            <div className="page-sub">
              View and manage tenant support requests
            </div>
          </div>
          <div className="topbar-right">
            <button
              className="btn btn-sm"
              style={{ background: "#185fa5", color: "#fff" }}
            >
              ⚙ Settings
            </button>
          </div>
        </div>

        <div className="content">
          {/* Stat Cards */}
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">🎫</div>
              <div className="stat-label">Total Tickets</div>
              <div className="stat-value">{ticketData?.totalTickets ?? 0}</div>
              <div className="stat-delta delta-neu">All time</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔵</div>
              <div className="stat-label">Open</div>
              <div className="stat-value">{ticketData?.open ?? 0}</div>
              <div className="stat-delta delta-dn">Awaiting action</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🟡</div>
              <div className="stat-label">In Progress</div>
              <div className="stat-value">{ticketData?.inProgress ?? 0}</div>
              <div className="stat-delta delta-neu">Being resolved</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Resolved</div>
              <div className="stat-value">{ticketData?.resolved ?? 0}</div>
              <div className="stat-delta delta-up">Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎤</div>
              <div className="stat-label">Voice Tickets</div>
              <div className="stat-value">{ticketData?.voiceTickets ?? 0}</div>
              <div className="stat-delta delta-neu">AI processed</div>
            </div>
          </div>

          <div className="card">
            {/* Filter Tabs + Search */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {FILTER_TABS.map((tab) => {
                  const isActive = activeFilter === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveFilter(tab.key)}
                      style={{
                        padding: "7px 16px",
                        fontSize: 12,
                        fontWeight: isActive ? 700 : 500,
                        border: `1px solid ${isActive ? "var(--teal)" : "var(--border-mid)"}`,
                        borderRadius: 99,
                        cursor: "pointer",
                        background: isActive
                          ? "var(--teal)"
                          : "var(--bg-primary)",
                        color: isActive ? "#fff" : "var(--text-secondary)",
                      }}
                    >
                      {tab.label}
                      {tab.count !== null && tab.count !== undefined && (
                        <span
                          style={{
                            background: isActive
                              ? "rgba(255,255,255,0.25)"
                              : "var(--border-light)",
                            borderRadius: 99,
                            padding: "1px 7px",
                            fontSize: 10,
                            marginLeft: 5,
                          }}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="🔍 Search tickets…"
                  value={searchInput}
                  onChange={handleSearchChange}
                  style={{
                    fontSize: 12,
                    padding: "6px 10px",
                    width: 180,
                    border: "0.5px solid var(--border-mid)",
                    borderRadius: 6,
                  }}
                />
                {searchInput && (
                  <button
                    className="btn btn-xs"
                    style={{ background: "#185fa5", color: "#fff" }}
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Ticket List */}
            <div>
              {listLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      border: "0.5px solid var(--border-light)",
                      borderRadius: "var(--radius-md)",
                      padding: 14,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {Array.from({ length: 3 }).map((_, j) => (
                        <div
                          key={j}
                          style={{
                            height: 14,
                            borderRadius: 6,
                            background:
                              "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 1.4s infinite",
                            width: j === 0 ? "30%" : j === 1 ? "60%" : "50%",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))
              ) : ticketData?.tickets?.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "var(--text-tertiary)",
                    padding: 32,
                    fontSize: 13,
                  }}
                >
                  No tickets found.
                </div>
              ) : (
                (ticketData?.tickets || []).map((ticket) => {
                  const statusBadge = getStatusBadge(ticket.status);
                  const priorityBadge = getPriorityBadge(ticket.priority);
                  return (
                    <div
                      key={ticket.ticketId}
                      onClick={() => openTicketDetail(ticket.ticketId)}
                      style={{
                        borderLeft: `4px solid ${getPriorityBorder(ticket.priority)}`,
                        border: "0.5px solid var(--border-light)",
                        borderRadius: "var(--radius-md)",
                        padding: 14,
                        marginBottom: 10,
                        background: "var(--bg-primary)",
                        cursor: "pointer",
                      }}
                    >
                      {/* Meta */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                            fontFamily: "monospace",
                          }}
                        >
                          {ticket.ticketNumber}
                        </span>
                        <span
                          className={`badge ${statusBadge.cls}`}
                          style={{ fontSize: 11, fontWeight: 700 }}
                        >
                          {statusBadge.label}
                        </span>
                        <span
                          className={`badge ${priorityBadge.cls}`}
                          style={{ fontSize: 10, fontWeight: 700 }}
                        >
                          {priorityBadge.label}
                        </span>
                        {ticket.categoryIcon && ticket.categoryName && (
                          <span
                            className="badge badge-gray"
                            style={{ fontSize: 10 }}
                          >
                            {ticket.categoryIcon} {ticket.categoryName}
                          </span>
                        )}
                        {ticket.ticketType === "Voice" && (
                          <span
                            className="badge badge-voice"
                            style={{ fontSize: 10 }}
                          >
                            🎤 Voice
                          </span>
                        )}
                      </div>

                      {/* Subject */}
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          marginBottom: 4,
                          color: "var(--text-primary)",
                        }}
                      >
                        {ticket.subject || "—"}
                      </div>

                      {/* Description */}
                      {ticket.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--text-secondary)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ticket.description}
                        </div>
                      )}

                      {/* Footer */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginTop: 10,
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>
                            {ticket.tenantName}
                          </span>
                          &nbsp;·&nbsp;{formatDate(ticket.createdAt)}
                          &nbsp;·&nbsp;💬 {ticket.messageCount} message
                          {ticket.messageCount !== 1 ? "s" : ""}
                        </div>
                        {/* <div
                          style={{ display: "flex", gap: 6 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {ticket.status === "Open" && (
                            <button
                              className="btn btn-xs"
                              style={{
                                background: "#185FA5",
                                color: "#fff",
                                borderColor: "#185FA5",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openTicketDetail(ticket.ticketId);
                              }}
                            >
                              ▶ Start
                            </button>
                          )}
                          {ticket.status === "InProgress" && (
                            <button
                              className="btn btn-xs"
                              style={{
                                background: "#DCFCE7",
                                color: "#15803D",
                                borderColor: "#86EFAC",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openTicketDetail(ticket.ticketId);
                              }}
                            >
                              ✓ Resolve
                            </button>
                          )}
                        </div> */}
                        <div
                          style={{ display: "flex", gap: 6 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {ticket.status === "Open" && (
                            <button
                              className="btn btn-xs"
                              style={{
                                background: "#185FA5",
                                color: "#fff",
                                borderColor: "#185FA5",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openTicketDetail(ticket.ticketId);
                              }}
                            >
                              ▶ Start
                            </button>
                          )}
                          {ticket.status === "InProgress" && (
                            <button
                              className="btn btn-xs"
                              style={{
                                background: "#DCFCE7",
                                color: "#15803D",
                                borderColor: "#86EFAC",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openTicketDetail(ticket.ticketId);
                              }}
                            >
                              ✓ Resolve
                            </button>
                          )}
                          {ticket.status === "Resolved" && (
                            <button
                              className="btn btn-xs"
                              style={{
                                background: "#F3F4F6",
                                color: "#6B7280",
                                borderColor: "#D1D5DB",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openTicketDetail(ticket.ticketId);
                              }}
                            >
                              🔒 Close
                            </button>
                          )}
                          {ticket.status === "Closed" && (
                            <span
                              className="badge badge-closed"
                              style={{ fontSize: 10 }}
                            >
                              🔒 Closed
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Detail Modal ── */}
      {showDetailModal && selectedTicket && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: "var(--bg-primary)",
              borderRadius: "var(--radius-lg)",
              width: "100%",
              maxWidth: 700,
              maxHeight: "94vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--border-light)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "var(--text-tertiary)",
                    }}
                  >
                    {selectedTicket.ticketNumber}
                  </span>
                  <span
                    className={`badge ${getStatusBadge(selectedTicket.status).cls}`}
                    style={{ fontSize: 11, fontWeight: 700 }}
                  >
                    {getStatusBadge(selectedTicket.status).label}
                  </span>
                  <span
                    className={`badge ${getPriorityBadge(selectedTicket.priority).cls}`}
                    style={{ fontSize: 10, fontWeight: 700 }}
                  >
                    {getPriorityBadge(selectedTicket.priority).label}
                  </span>
                  {selectedTicket.categoryIcon &&
                    selectedTicket.categoryName && (
                      <span
                        className="badge badge-gray"
                        style={{ fontSize: 10 }}
                      >
                        {selectedTicket.categoryIcon}{" "}
                        {selectedTicket.categoryName}
                      </span>
                    )}
                  {selectedTicket.ticketType === "Voice" && (
                    <span
                      className="badge badge-voice"
                      style={{ fontSize: 10 }}
                    >
                      🎤 Voice
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--text-primary)",
                  }}
                >
                  {selectedTicket.subject}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-tertiary)",
                    marginTop: 3,
                  }}
                >
                  {selectedTicket.tenantName} &nbsp;·&nbsp; Created:{" "}
                  {formatDateTime(selectedTicket.createdAt)}
                </div>
              </div>
              {/* Action buttons in header */}
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  flexShrink: 0,
                  marginLeft: 12,
                }}
              >
                {selectedTicket.status === "Open" && (
                  <button
                    className="btn btn-sm"
                    style={{
                      background: "#185FA5",
                      color: "#fff",
                      borderColor: "#185FA5",
                    }}
                    onClick={() => handleStatusAction("start")}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "..." : "▶ Start"}
                  </button>
                )}
                {selectedTicket.status === "InProgress" && (
                  <button
                    className="btn btn-sm"
                    style={{
                      background: "#DCFCE7",
                      color: "#15803D",
                      borderColor: "#86EFAC",
                    }}
                    onClick={() => handleStatusAction("resolve")}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "..." : "✓ Resolve"}
                  </button>
                )}
                {selectedTicket.status === "Resolved" && (
                  <button
                    className="btn btn-sm"
                    style={{
                      background: "#F3F4F6",
                      color: "#6B7280",
                      borderColor: "#D1D5DB",
                    }}
                    onClick={() => handleStatusAction("close")}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "..." : "🔒  Close"}
                  </button>
                )}
                <button
                  onClick={closeDetailModal}
                  style={{
                    border: "none",
                    background: "none",
                    fontSize: 24,
                    cursor: "pointer",
                    color: "var(--text-tertiary)",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Part 1 — Description fixed */}
            <div style={{ padding: "18px 20px 0", flexShrink: 0 }}>
              <div
                className="info-box"
                style={{ marginBottom: 14, fontSize: 12, lineHeight: "1.6" }}
              >
                <strong>📋 Description:</strong> {selectedTicket.description}
              </div>
              {selectedTicket.ticketType === "Voice" &&
                selectedTicket.audioUrl && (
                  <div
                    style={{
                      background: "var(--purple-light)",
                      borderRadius: "var(--radius-md)",
                      padding: "12px 14px",
                      marginBottom: 14,
                      border: "0.5px solid #C4B5FD",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--purple)",
                        marginBottom: 6,
                      }}
                    >
                      🎤 Voice Ticket — AI Transcript
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#3C3489",
                        lineHeight: "1.6",
                      }}
                    >
                      {selectedTicket.description}
                    </div>
                  </div>
                )}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-tertiary)",
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                  marginBottom: 8,
                }}
              >
                Conversation
              </div>
            </div>

            {/* Part 2 — Chat scrollable */}
            <div
              ref={chatContainerRef}
              style={{ overflowY: "auto", flex: 1, padding: "0 20px 18px" }}
            >
              {messagesLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        height: 14,
                        borderRadius: 6,
                        background:
                          "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                        width: "60%",
                      }}
                    />
                  </div>
                ))
              ) : messages.length === 0 ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-tertiary)",
                    padding: "12px 0",
                  }}
                >
                  No messages yet.
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {messages.map((msg) => {
                    const isOwner = msg.senderType === "Owner";
                    return (
                      <div
                        key={msg.messageId}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isOwner ? "flex-end" : "flex-start",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                            marginBottom: 3,
                            textAlign: isOwner ? "right" : "left",
                          }}
                        >
                          {isOwner ? "You (Owner)" : selectedTicket.tenantName}
                        </div>
                        <div
                          style={{
                            maxWidth: "75%",
                            padding: "10px 14px",
                            borderRadius: 12,
                            fontSize: 13,
                            lineHeight: "1.5",
                            background: isOwner
                              ? "var(--blue)"
                              : "var(--bg-secondary)",
                            color: isOwner ? "#fff" : "var(--text-primary)",
                            borderBottomRightRadius: isOwner ? 4 : 12,
                            borderBottomLeftRadius: isOwner ? 12 : 4,
                          }}
                        >
                          {msg.message}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                            marginTop: 3,
                            textAlign: isOwner ? "right" : "left",
                          }}
                        >
                          {formatDateTime(msg.createdAt)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reply Box */}
            <div
              style={{
                padding: "14px 20px",
                borderTop: "1px solid var(--border-light)",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <textarea
                  rows={2}
                  placeholder="Type your reply here…"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  style={{
                    flex: 1,
                    resize: "none",
                    fontSize: 13,
                    padding: "10px 12px",
                    border: "1px solid var(--border-mid)",
                    borderRadius: "var(--radius-md)",
                  }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSendReply}
                  disabled={replyLoading}
                  style={{ flexShrink: 0, padding: "10px 16px" }}
                >
                  {replyLoading ? "..." : "Send 📤"}
                </button>
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--text-tertiary)",
                  marginTop: 5,
                }}
              >
                Press Send to post your reply. Tenant will be notified.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
